import { Box, Card, CardContent, FormLabel, Typography } from '@mui/joy';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IContactGroupAddress, ISupplier } from 'contacts/types';
import { Button } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { useFormContext } from 'core/states';
import { CorporatePartAssignVendor, CorporatePartAssignVendorFormData } from 'corporate/components';
import { PriceBasedOn } from 'quotes/enums';
import { Color, Level, Variant } from 'theme/enums';

import { useCostFormFieldsContext, usePricingStateContext } from '../CostFormFields';

interface IProps {
  externalSupplier?: Partial<ISupplier>;
  disabled?: boolean;
  isAlternateVersionEnabled?: boolean;
  level?: number;
}

export const VendorFormField = ({
  externalSupplier = null,
  disabled,
  isAlternateVersionEnabled = true,
  level = 0,
}: IProps) => {
  const [isAssignVendorOpen, setIsAssignVendorOpen] = useState(false);

  const { t } = useTranslation();

  const { watch, setValue, getValues } = useFormContext();

  const { onPriceBasedOnChange } = useCostFormFieldsContext();
  const {
    calculatePrices,
    calculateStandardCostForReplacementCost,
    clearTypedCostValidity,
    resetSpaSelectionAvailable,
    setIsSelectedSpaNotOffered,
    setCostData,
  } = usePricingStateContext();

  const vendorNameValue = watch('companyManufacturerName');
  const vendorIdValue = watch('companyManufacturerId');
  const accountsPayableIdValue = watch('accountsPayableId');

  const getDefaultValues = () => {
    if (isAlternateVersionEnabled) {
      return {
        vendorLocation: accountsPayableIdValue
          ? {
              name: vendorNameValue || '',
              accountsPayableId: accountsPayableIdValue || '',
            }
          : null,
        vendorName: vendorNameValue || '',
        vendorId: vendorIdValue || '',
        accountsPayableId: accountsPayableIdValue || '',
      };
    }

    return {
      vendor: vendorIdValue
        ? {
            vendorName: vendorNameValue || '',
            vendorId: vendorIdValue || '',
          }
        : null,
      vendorName: vendorNameValue || '',
      vendorId: vendorIdValue || '',
    };
  };

  const onVendorTransition = async (vendorLocation?: Partial<IContactGroupAddress>) => {
    const partValue = getValues('part');
    const isShipAndDebitVendor = vendorLocation?.isShipAndDebit || false;

    // The replacement cost card visibility and validation derive from
    // part.supplier.isShipAndDebit, so it must reflect the newly selected
    // vendor before the line is saved.
    setValue('part', {
      ...partValue,
      supplier: {
        ...partValue?.supplier,
        isShipAndDebit: isShipAndDebitVendor,
        primaryContact: vendorLocation?.primaryContact ?? null,
        purchasingAttributes: vendorLocation?.purchasingAttributes ?? [],
      },
    });

    // Drop any custom override from the previous vendor so the freshly fetched
    // external values seed the card.
    setValue('isReplacementCostCustom', false);
    clearTypedCostValidity();

    // The pick is the previous vendor's; clearing its cost lets the recalculation source the line.
    if (getValues('selectedSpa')) {
      setValue('selectedSpa', null);
      setValue('purchaseCost', null);
      setValue('costSource.vendorQuoteNumber', null);
    }

    // Externally-sourced costs are vendor-specific, so none of the previous vendor's may survive
    // into the response below — externalPurchaseCost above all, since the Overwrite-off revert puts
    // it back on the wire and the save payload carries it verbatim. Mirrors the part-change reset
    // in UpsertLine/state.tsx; the response is the only writer that may repopulate these.
    setCostData((prev) => ({
      ...prev,
      externalPurchaseCost: null,
      externalRepCostEndDate: null,
      externalRepCostRegistrationNum: null,
      externalRepCostSource: null,
      externalRepCostSourceName: null,
      externalReplacementCost: null,
      externalSpaCost: null,
      externalSpaCostEndDate: null,
      externalSpaCostRegistrationNum: null,
      externalSpaCostSource: null,
      externalSpaCostSourceName: null,
      selectedSpa: null,
    }));
    // The engine answers per vendor; a failed recalculation must not leave the previous one's.
    resetSpaSelectionAvailable();
    setIsSelectedSpaNotOffered(false);

    // Re-fetch pricing for the newly selected vendor. handleVendorSelect has
    // already written the new accountsPayableId, which the pricing call sends as
    // the external supplier — so cost and replacement cost are vendor-specific
    // (processCostResponse seeds replacementCost / number from the response).
    await calculatePrices();

    if (!isShipAndDebitVendor) {
      // Replacement cost is not available for a non ship-and-debit vendor: clear
      // it (and the stale standard cost) and make sure the price is based on
      // quoted cost, not a leftover replacement value.
      setValue('replacementCost', null);
      setValue('replacementCostNumber', null);
      setValue('replacementStandardCost', null);

      setValue('part', {
        ...getValues('part'),
        externalReplacementCost: null,
        externalRepCostRegistrationNum: null,
      });

      if (PriceBasedOn.REPLACEMENT === getValues('priceBasedOn')) {
        await onPriceBasedOnChange({ target: { value: PriceBasedOn.QUOTED } });
      }

      return;
    }

    // Ship & debit vendor: keep the frozen part snapshot in sync with the newly
    // fetched replacement cost so override-restore and validation read the new
    // vendor's value instead of the load-time one.
    const newReplacementCost = Number(getValues('replacementCost')) || null;

    setValue('part', {
      ...getValues('part'),
      externalReplacementCost: newReplacementCost,
      externalRepCostRegistrationNum: getValues('replacementCostNumber') ?? null,
    });

    if (newReplacementCost) {
      // Derive the replacement standard cost (and recompute the replacement-based
      // price/margin when that pricing mode is active) for the new value.
      await calculateStandardCostForReplacementCost(newReplacementCost);

      return;
    }

    // New vendor has no replacement cost — it can no longer drive the price.
    setValue('replacementStandardCost', null);

    if (PriceBasedOn.REPLACEMENT === getValues('priceBasedOn')) {
      await onPriceBasedOnChange({ target: { value: PriceBasedOn.QUOTED } });
    }
  };

  const handleVendorSelect = async (data: CorporatePartAssignVendorFormData) => {
    const vendorChanged = (data.accountsPayableId || '') !== (accountsPayableIdValue || '');

    setValue('companyManufacturerName', data.vendorName);
    setValue('companyManufacturerId', data.vendorId);

    setIsAssignVendorOpen(false);

    if (isAlternateVersionEnabled) {
      setValue('accountsPayableId', data.accountsPayableId || '');

      if (vendorChanged) {
        await onVendorTransition(data.vendorLocation);
      }
    }

    return { success: true };
  };

  return (
    <>
      <Card color={Color.NEUTRAL} variant={Variant.PLAIN}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <FormLabel sx={{ mb: 0 }}>{t('labelVendor')}</FormLabel>
            {!disabled && (
              <Button
                text={t('buttonLabelEdit')}
                variant={Variant.PLAIN}
                onClick={() => setIsAssignVendorOpen(true)}
              />
            )}
          </Box>

          <Typography level={Level.BODY_SM} fontWeight={600}>
            {vendorNameValue || DEFAULT_PLACEHOLDER}
          </Typography>
          <Typography level={Level.BODY_XS} fontWeight={600}>
            {(isAlternateVersionEnabled ? accountsPayableIdValue : vendorIdValue) ||
              DEFAULT_PLACEHOLDER}
          </Typography>
        </CardContent>
      </Card>

      <CorporatePartAssignVendor
        isOpen={isAssignVendorOpen}
        onClose={() => setIsAssignVendorOpen(false)}
        onSubmit={handleVendorSelect}
        defaultValues={getDefaultValues()}
        externalSupplier={externalSupplier}
        isEditable
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        level={level + 1}
      />
    </>
  );
};
